<template>
  <div class="rewrite-page studio-layout">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>洗稿</span>
          <el-tag type="info" size="small">换语气换说法，本质不变</el-tag>
        </div>
      </template>

      <el-form :model="form" label-width="120px">
        <el-form-item label="原文内容" required>
          <el-input
            v-model="form.originalContent"
            type="textarea"
            :rows="12"
            placeholder="粘贴需要洗稿的原文，这是唯一必填项"
          />
          <div class="char-count">{{ form.originalContent.length }} 字</div>
        </el-form-item>

        <el-collapse v-model="optionalOpen" class="optional-collapse">
          <el-collapse-item title="高级选项（可选）" name="optional">
            <el-form-item label="洗稿强度">
              <el-radio-group v-model="form.intensity">
                <el-radio value="light">轻度改写</el-radio>
                <el-radio value="medium">中度改写</el-radio>
                <el-radio value="deep">深度改写</el-radio>
              </el-radio-group>
            </el-form-item>
            <el-form-item label="篇幅长度">
              <el-radio-group v-model="form.length">
                <el-radio value="keep">保持原样</el-radio>
                <el-radio value="shorter">精简</el-radio>
                <el-radio value="longer">扩写</el-radio>
              </el-radio-group>
            </el-form-item>
            <el-form-item label="关键词保留">
              <el-input
                v-model="form.keywords"
                placeholder="多个关键词用逗号或空格分隔，洗稿时会尽量保留"
              />
            </el-form-item>
            <el-form-item label="补充要求">
              <el-input
                v-model="form.customPrompt"
                type="textarea"
                :rows="3"
                placeholder="例如：语气更年轻化 / 避免使用「其实」等词"
              />
            </el-form-item>
          </el-collapse-item>
        </el-collapse>

        <el-form-item>
          <el-button
            type="primary"
            :loading="generating"
            :disabled="!form.originalContent.trim()"
            @click="handleGenerate"
          >
            开始洗稿（生成 3 个版本）
          </el-button>
          <el-button @click="goHistory">去文章管理</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-card v-if="versions.length" class="result-card">
      <template #header>
        <div class="card-header">
          <span>洗稿结果</span>
          <el-tag type="success" size="small">已生成 {{ versions.length }} 个版本并保存</el-tag>
        </div>
      </template>

      <el-tabs v-model="activeTab">
        <el-tab-pane
          v-for="v in versions"
          :key="v.version_index"
          :label="`版本 ${v.version_index}`"
          :name="String(v.version_index)"
        >
          <div class="version-toolbar">
            <span class="version-title">{{ v.title }}</span>
            <div class="version-actions">
              <el-tag v-if="v.article_id" size="small" type="info">文章 #{{ v.article_id }}</el-tag>
              <el-button size="small" @click="copyContent(v.content)">复制正文</el-button>
            </div>
          </div>
          <el-input
            v-model="v.content"
            type="textarea"
            :rows="18"
            readonly
            class="result-textarea"
          />
        </el-tab-pane>
      </el-tabs>

      <el-alert
        v-for="(err, idx) in errors"
        :key="idx"
        :title="`版本 ${err.version_index} 失败：${err.error}`"
        type="warning"
        :closable="false"
        show-icon
        class="err-alert"
      />
    </el-card>
  </div>
</template>

<script>
import { ref, reactive } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { rewriteApi } from '../api'

export default {
  name: 'Rewrite',
  setup() {
    const router = useRouter()
    const generating = ref(false)
    const optionalOpen = ref([])
    const activeTab = ref('1')
    const versions = ref([])
    const errors = ref([])

    const form = reactive({
      originalContent: '',
      intensity: 'medium',
      length: 'keep',
      keywords: '',
      customPrompt: '',
    })

    const handleGenerate = async () => {
      if (!form.originalContent.trim()) {
        ElMessage.warning('请先输入原文内容')
        return
      }
      generating.value = true
      versions.value = []
      errors.value = []
      try {
        const res = await rewriteApi.generate({
          original_content: form.originalContent.trim(),
          intensity: form.intensity,
          length: form.length,
          keywords: form.keywords.trim(),
          custom_prompt: form.customPrompt.trim(),
          save_to_db: true,
        })
        versions.value = res.versions || []
        errors.value = res.errors || []
        if (versions.value.length) {
          activeTab.value = String(versions.value[0].version_index)
          ElMessage.success(`已生成 ${versions.value.length} 个版本，已保存到文章管理`)
        } else {
          ElMessage.error('未能生成任何版本')
        }
      } catch (e) {
        ElMessage.error(e?.message || '洗稿失败')
      } finally {
        generating.value = false
      }
    }

    const copyContent = async (text) => {
      try {
        await navigator.clipboard.writeText(text || '')
        ElMessage.success('已复制到剪贴板')
      } catch {
        ElMessage.error('复制失败，请手动选择复制')
      }
    }

    const goHistory = () => router.push('/content/library')

    return {
      form,
      optionalOpen,
      generating,
      versions,
      errors,
      activeTab,
      handleGenerate,
      copyContent,
      goHistory,
    }
  },
}
</script>

<style scoped>
.rewrite-page {
  padding: 0;
}
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.char-count {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
  text-align: right;
}
.optional-collapse {
  margin-bottom: 12px;
  border: none;
}
.result-card {
  margin-top: 20px;
}
.version-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  gap: 12px;
}
.version-title {
  font-weight: 600;
  color: #303133;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.version-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.result-textarea :deep(textarea) {
  font-family: inherit;
  line-height: 1.6;
}
.err-alert {
  margin-top: 12px;
}
</style>
